import { z } from "zod";

export const FontStyleDto = z.enum(["normal", "italic"]);

// Font categories as returned by the Google Fonts API.
export const FontCategoryDto = z.enum([
  "sans-serif",
  "serif",
  "display",
  "handwriting",
  "monospace",
]);

export const FontVariantDto = z.object({
  weight: z.number(),
  style: FontStyleDto,
  url: z.string(),
});

export const FontMetadataDto = z.object({
  previewSrc: z.string(),
  category: z.string().optional(),
  subsets: z.array(z.string()).optional(),
  variants: z.array(FontVariantDto),
});

export const FontDto = z.object({
  externalId: z.string(),
  family: z.string(),
  teamExternalId: z.string().nullable(),
  metadata: FontMetadataDto,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const CreateGoogleFontDto = z.object({
  family: z.string().min(1).max(100),
  text: z.string().min(1).max(200).optional(),
});

export const CreateFontDto = z.object({
  fonts: z.array(CreateGoogleFontDto).min(1).max(50),
});

export const CreateFontResponseDto = z.object({
  fonts: z.array(FontDto),
  created: z.number(),
  skipped: z.number(),
});

export const SeedGoogleFontsDto = z.object({
  limit: z.number().int().min(1).max(50).default(10),
});

export const SeedGoogleFontsResponseDto = CreateFontResponseDto.extend({
  remaining: z.number(),
});

export const SUPPORTED_FONT_MIME_TYPES = [
  "font/ttf",
  "font/otf",
  "font/woff",
  "font/woff2",
] as const;

export type SupportedFontMimeType = (typeof SUPPORTED_FONT_MIME_TYPES)[number];

export const UploadCustomFontDto = z.object({
  family: z.string().min(1).max(100),
  text: z.string().min(1).max(200).optional(),
});

// Superuser upload can target any team via its externalId.
export const AdminUploadCustomFontDto = UploadCustomFontDto.extend({
  teamExternalId: z.string().min(1),
});

export const UploadCustomFontResponseDto = FontDto;

export const ListFontsResponseDto = z.object({
  fonts: z.array(FontDto),
});

export const FontOwnershipFilterDto = z.enum(["ALL", "USER", "LIBRARY"]);

export const GetPagedFontsQueryDto = z.object({
  pageSize: z.coerce.number().min(1).max(100).default(25),
  search: z.string().optional(),
  ownership: FontOwnershipFilterDto.optional(),
  category: FontCategoryDto.optional(),
  cursorIsTeamFont: z
    .enum(["true", "false"])
    .transform((value) => value === "true")
    .optional(),
  cursorFamily: z.string().optional(),
  cursorExternalId: z.string().optional(),
});

export const PagedFontsResponseDto = z.object({
  fonts: z.array(FontDto),
  nextCursor: z
    .object({
      isTeamFont: z.boolean(),
      family: z.string(),
      externalId: z.string(),
    })
    .optional(),
});

export const GetFontsByExternalIdsBodyDto = z.object({
  externalIds: z.array(z.string()),
});

export const DeleteFontResponseDto = z.object({
  deletedAt: z.string(),
});

export type FontStyleDto = z.infer<typeof FontStyleDto>;
export type FontCategoryDto = z.infer<typeof FontCategoryDto>;
export type FontVariantDto = z.infer<typeof FontVariantDto>;
export type FontMetadataDto = z.infer<typeof FontMetadataDto>;
export type FontDto = z.infer<typeof FontDto>;
export type CreateGoogleFontDto = z.infer<typeof CreateGoogleFontDto>;
export type CreateFontDto = z.infer<typeof CreateFontDto>;
export type CreateFontResponseDto = z.infer<typeof CreateFontResponseDto>;
export type SeedGoogleFontsDto = z.infer<typeof SeedGoogleFontsDto>;
export type SeedGoogleFontsResponseDto = z.infer<
  typeof SeedGoogleFontsResponseDto
>;
export type UploadCustomFontDto = z.infer<typeof UploadCustomFontDto>;
export type AdminUploadCustomFontDto = z.infer<typeof AdminUploadCustomFontDto>;
export type UploadCustomFontResponseDto = z.infer<
  typeof UploadCustomFontResponseDto
>;
export type ListFontsResponseDto = z.infer<typeof ListFontsResponseDto>;
export type FontOwnershipFilterDto = z.infer<typeof FontOwnershipFilterDto>;
export type GetPagedFontsQueryDto = z.infer<typeof GetPagedFontsQueryDto>;
export type PagedFontsResponseDto = z.infer<typeof PagedFontsResponseDto>;
export type DeleteFontResponseDto = z.infer<typeof DeleteFontResponseDto>;
